<template>
  <b-modal 
    :title="helper.trans('labels.search_deposits')"
    :modelValue="isVisible"
    size="lg"
    @update:modelValue="isVisible = $event"
    @hide="isVisible = false"
  >
    <div style="display: none;">{{ helper.trans('button.close') }}</div>
          <div class="row">
            <div class="col-12">
              <div class="form-group">
                <label for="transactionHash" class="form-label text-capitalize-first">{{ helper.trans('labels.transaction_hash') }}</label>
                <FormInputWrapper v-slot="parentProps" :errors="getErrors('trx')">
                  <input 
                    v-model="form.trx" 
                    :class="parentProps.class" 
                    type="text" 
                    class="form-control" 
                    id="transactionHash" 
                    :placeholder="helper.trans('labels.enter_transaction_hash')"
                    @keyup.enter="searchDeposit"
                  >
                </FormInputWrapper>
              </div>
            </div>
          </div>

          <div class="row mt-3" v-if="searchResult">
            <div class="col-12">
              <div class="card">
                <div class="card-header">
                  <h6 class="mb-0">{{ helper.trans('labels.search_result') }}</h6>
                </div>
                <div class="card-body">
                  <div class="row">
                    <div class="col-md-6">
                      <p class="mb-2"><strong>{{ translations.txId }}:</strong><br>{{ searchResult.txId }}</p>
                      <p class="mb-2"><strong>{{ translations.walletAddress }}:</strong><br>{{ searchResult.walletAddress || '-' }}</p>
                      <p class="mb-2"><strong>{{ translations.fromAddress }}:</strong><br>{{ searchResult.fromAddress || '-' }}</p>
                    </div>
                    <div class="col-md-6">
                      <p class="mb-2"><strong>{{ translations.amount }}:</strong><br>
                        <span v-if="searchResult.amount">
                          {{ numberFormatter.formatMoney(searchResult.amount, 0) }}
                        </span>
                        <span v-else>-</span>
                      </p>
                      <p class="mb-2"><strong>{{ translations.status }}:</strong><br>
                        <span v-if="searchResult.status" :class="getStatusClass(searchResult.status)">
                          {{ helper.trans(`deposit_status.${searchResult.status.toLowerCase()}`) }}
                        </span>
                        <span v-else>-</span>
                      </p>
                      <p class="mb-2"><strong>{{ translations.depositId }}:</strong><br>
                        <span v-if="searchResult.depositId">{{ searchResult.depositId.substring(0, 8) }}...</span>
                        <span v-else>-</span>
                      </p>
                    </div>
                  </div>
                  
                  <div class="row mt-3" v-if="searchResult.found && !searchResult.depositId">
                    <div class="col-12">
                      <div class="alert alert-info">
                        {{ helper.trans('labels.deposit_not_credited') }}
                      </div>
                      <button 
                        type="button" 
                        @click="creditDeposit" 
                        class="btn btn-success"
                        :disabled="isLoading"
                      >
                        <i v-if="isLoading" class="fa fa-spinner fa-spin me-1"></i>
                        {{ helper.trans('button.credit_deposit') }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="row mt-3" v-if="searchResult && !searchResult.found">
            <div class="col-12">
              <div class="alert alert-warning">
                {{ helper.trans('labels.transaction_not_found') }}
              </div>
            </div>
          </div>

    <template #footer>
      <button type="button" @click="isVisible = false" class="btn btn-secondary">{{ helper.trans('button.close') }}</button>
      <button type="button" @click="searchDeposit" class="btn btn-primary" :disabled="!form.trx || isLoading">
        <i v-if="isLoading" class="fa fa-spinner fa-spin me-1"></i>
        {{ helper.trans('button.search') }}
      </button>
    </template>
  </b-modal>
</template>

<script lang="ts">
import { defineComponent, reactive, ref, computed } from 'vue'
import { helper } from "@/service/CommonHelper"
import { useNotification } from "@kyvg/vue3-notification"
import { numberFormatter } from "@/service/NumberFormatter"
import FormInputWrapper from "@/components/FormInputWrapper.vue"
import APIClient from "@/service/APIClient"
import { BModal } from "bootstrap-vue-3"

interface SearchDepositRequestDTO {
  trx: string
}

interface SearchDepositResponseDTO {
  txId: string
  walletAddress: string | null
  fromAddress: string | null
  amount: {
    amount: string
    currency: string
  } | null
  status: string | null
  depositId: string | null
  found: boolean
}

interface CreditDepositResponseDTO {
  success: boolean
  message: string
  txId: string
  walletId: number
  amount: string
  fromAddress: string
  toAddress: string
}

export default defineComponent({
  name: 'SearchDepositModal',
  components: {
    FormInputWrapper,
    BModal
  },
  emits: ['deposit:credited'],
  setup(props, { emit }) {
    const api = new APIClient()
    const notification = useNotification()
    const isLoading = ref(false)
    const isVisible = ref(false)
    const searchResult = ref<SearchDepositResponseDTO | null>(null)
    
    const state = reactive({
      errors: {} as Record<string, string[]>
    })

    const form = reactive<SearchDepositRequestDTO>({
      trx: ''
    })

    const getErrors = (field: string): string[] => {
      return state.errors[field] || []
    }

    const getStatusClass = (status: string): string => {
      const statusLower = status.toLowerCase()
      switch (statusLower) {
        case 'completed':
          return 'text-success'
        case 'pending':
          return 'text-warning'
        case 'rejected':
          return 'text-danger'
        default:
          return 'text-secondary'
      }
    }

    const searchDeposit = async () => {
      if (!form.trx.trim()) {
        notification.notify({
          type: 'error',
          title: helper.trans('error'),
          text: helper.trans('enter_transaction_hash')
        })
        return
      }

      isLoading.value = true
      state.errors = {}
      searchResult.value = null

      try {
        const result = await api.searchDeposit({ trx: form.trx.trim() })
        searchResult.value = result
      } catch (error: any) {
        console.error('Search deposit error:', error)
        if (error.response?.data?.errors) {
          state.errors = error.response.data.errors
        } else {
          notification.notify({
            type: 'error',
            title: helper.trans('error'),
            text: error.response?.data?.message || helper.trans('search_error')
          })
        }
      } finally {
        isLoading.value = false
      }
    }

    const creditDeposit = async () => {
      if (!searchResult.value?.txId) return

      isLoading.value = true
      try {
        const result = await api.creditDeposit({ trx: searchResult.value.txId }) as CreditDepositResponseDTO
        
        if (result.success) {
          notification.notify({
            type: 'success',
            title: helper.trans('success'),
            text: result.message || helper.trans('deposit_credited_successfully')
          })
          emit('deposit:credited')
          isVisible.value = false
        } else {
          notification.notify({
            type: 'error',
            title: helper.trans('error'),
            text: result.message || helper.trans('credit_error')
          })
        }
      } catch (error: any) {
        notification.notify({
          type: 'error',
          title: helper.trans('error'),
          text: error.response?.data?.message || helper.trans('credit_error')
        })
      } finally {
        isLoading.value = false
      }
    }

    const show = () => {
      form.trx = ''
      searchResult.value = null
      state.errors = {}
      isVisible.value = true
    }

    const translations = computed(() => ({
      txId: helper.trans('labels.tx_id'),
      walletAddress: helper.trans('labels.wallet_address'),
      fromAddress: helper.trans('labels.from_address.usdt'),
      amount: helper.trans('labels.amount'),
      status: helper.trans('labels.status'),
      depositId: helper.trans('labels.deposit_id')
    }))

    return {
      form,
      isLoading,
      isVisible,
      searchResult,
      helper,
      notification,
      numberFormatter,
      getErrors,
      getStatusClass,
      searchDeposit,
      creditDeposit,
      show,
      translations
    }
  }
})
</script>
